import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/theme";
import { api } from "@/api/client";
import { GoogleButton } from "@/components/GoogleButton";
import { AmbientBackground } from "@/components/AmbientBackground";

export default function SignUp() {
  const { signUp, selectedBusinessId } = useAuth();
  const nav = useNavigate();
  const theme = getTheme(selectedBusinessId);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "customer", business_id: "baking_jays" });
  const [loading, setLoading] = useState(false);
  const [bakerSlots, setBakerSlots] = useState(null);

  useEffect(() => {
    api.bakerSlots().then(setBakerSlots).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedBusinessId) setForm((f) => ({ ...f, business_id: selectedBusinessId }));
  }, [selectedBusinessId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      // Bakers choose their main side explicitly; customers keep the side they're browsing.
      const business_id = form.role === "baker" ? form.business_id : selectedBusinessId;
      const u = await signUp({ ...form, email: form.email.trim(), business_id });
      toast.success(`Welcome, ${u.name}!`);
      nav(u.role === "baker" ? "/manage" : "/shop");
    } catch (err) {
      toast.error(err.message || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const bakerFull = bakerSlots && bakerSlots.available <= 0;

  return (
    <div className="min-h-screen relative">
      <AmbientBackground />
      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 hero-gradient">
        <Link to="/" className="font-display text-2xl" style={{ color: "var(--hero-fg)" }}>
          {theme.name}
        </Link>
        <div>
          <h2 className="font-display text-4xl leading-tight" style={{ color: "var(--hero-fg)" }}>
            Join the guest list.
          </h2>
          <p className="mt-3 max-w-sm" style={{ color: "color-mix(in srgb, var(--hero-fg) 75%, transparent)" }}>
            Create an account to place orders, earn loyalty stamps, and track every bake.
          </p>
        </div>
        <div className="text-xs" style={{ color: "color-mix(in srgb, var(--hero-fg) 60%, transparent)" }}>
          {theme.kind} · Order online
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="signup-form">
          <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>
            Create account
          </h1>

          <div className="mt-5 grid grid-cols-2 gap-2 p-1 rounded-xl" style={{ background: "var(--surface-2)" }}>
            {["customer", "baker"].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setForm((f) => ({ ...f, role: r }))}
                disabled={r === "baker" && bakerFull}
                data-testid={`role-${r}`}
                className="py-2 rounded-lg text-sm font-semibold capitalize transition disabled:opacity-40"
                style={
                  form.role === r
                    ? { background: "var(--surface)", color: "var(--ink)", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }
                    : { color: "var(--muted)" }
                }
              >
                {r === "baker" ? "Baker / Chef" : "Customer"}
              </button>
            ))}
          </div>
          {form.role === "baker" && bakerSlots && (
            <p className="mt-2 text-xs" style={{ color: bakerFull ? "#b91c1c" : "var(--muted)" }}>
              {bakerFull
                ? `All baker slots are taken (max ${bakerSlots.max}).`
                : `${bakerSlots.available} of ${bakerSlots.max} baker slots available.`}
            </p>
          )}

          {form.role === "baker" && (
            <div className="mt-4">
              <div className="text-sm font-semibold mb-1.5" style={{ color: "var(--ink)" }}>Your main side</div>
              <div className="grid grid-cols-2 gap-2 p-1 rounded-xl" style={{ background: "var(--surface-2)" }}>
                {[
                  { id: "baking_jays", label: "Baking" },
                  { id: "rubys", label: "Catering" },
                ].map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, business_id: b.id }))}
                    data-testid={`signup-side-${b.id}`}
                    className="py-2 rounded-lg text-sm font-semibold transition"
                    style={form.business_id === b.id ? { background: "var(--surface)", color: "var(--ink)", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" } : { color: "var(--muted)" }}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-xs" style={{ color: "var(--muted)" }}>The dashboard you&apos;ll open on. You can still switch sides anytime.</p>
            </div>
          )}

          <label className="block mt-4 text-sm font-semibold" style={{ color: "var(--ink)" }}>
            Name
            <input
              required
              value={form.name}
              onChange={set("name")}
              data-testid="signup-name"
              className="mt-1.5 w-full rounded-xl px-4 py-3 outline-none"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
            />
          </label>
          <label className="block mt-4 text-sm font-semibold" style={{ color: "var(--ink)" }}>
            Email
            <input
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              data-testid="signup-email"
              className="mt-1.5 w-full rounded-xl px-4 py-3 outline-none"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
            />
          </label>
          <label className="block mt-4 text-sm font-semibold" style={{ color: "var(--ink)" }}>
            Password
            <input
              type="password"
              required
              value={form.password}
              onChange={set("password")}
              data-testid="signup-password"
              className="mt-1.5 w-full rounded-xl px-4 py-3 outline-none"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
              placeholder="At least 8 characters"
            />
          </label>

          <button
            type="submit"
            disabled={loading || (form.role === "baker" && bakerFull)}
            data-testid="signup-submit"
            className="btn-brand w-full mt-6 rounded-xl py-3 font-semibold disabled:opacity-60"
          >
            {loading ? "Creating…" : "Create account"}
          </button>

          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1" style={{ background: "var(--border)" }} />
            <span className="text-xs" style={{ color: "var(--muted)" }}>or</span>
            <span className="h-px flex-1" style={{ background: "var(--border)" }} />
          </div>
          <GoogleButton label="Sign up with Google" />
          <p className="mt-2 text-xs text-center" style={{ color: "var(--muted)" }}>
            Google sign-up creates a customer account.
          </p>

          <p className="mt-5 text-sm text-center" style={{ color: "var(--muted)" }}>
            Already have an account?{" "}
            <Link to="/sign-in" className="font-semibold" style={{ color: "var(--brand)" }}>
              Sign in
            </Link>
          </p>
        </form>
      </div>
      </div>
    </div>
  );
}
